import React from 'react';
import {
  CreditApplicationColumnType,
  CreditApplicationFields,
  CreditApplicationFieldsValues,
  CreditApplicationStep,
  ConditionalStepField,
} from '@dill/dill-shared';

const COLUMN_COUNT: Record<CreditApplicationColumnType, number> = {
  ONE_COLUMN: 1,
  TWO_COLUMNS: 2,
  THREE_COLUMNS: 3,
};
const GRID_COLS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 md:grid-cols-2',
  3: 'grid-cols-1 md:grid-cols-3',
};

type AnswerField = Pick<
  CreditApplicationFields,
  'id' | 'name' | 'isRequired' | 'fromField' | 'prefix' | 'currencyDecimalPlaces'
>;
type Answer = { field: AnswerField; value: any; conditional?: React.ReactNode };

const AnswerValue = ({ value, field }: { value: any; field?: AnswerField }) => {
  // Currency with commas and the field's decimal places, e.g. $12,500.00.
  if (field?.fromField === 'CURRENCY' && value !== null && value !== '' && !isNaN(Number(value))) {
    const decimals = field.currencyDecimalPlaces ?? 2;
    return (
      <p className="text-sm text-gray-900">
        {`${field.prefix || '$'}${Number(value).toLocaleString('en-US', {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        })}`}
      </p>
    );
  }
  if (value && typeof value === 'object' && typeof value.url === 'string') {
    return (
      <a
        href={value.url}
        target="_blank"
        rel="noreferrer"
        className="text-sm PRIMARY_500-CLR underline break-all">
        {value.name || 'View file'}
      </a>
    );
  }
  if (typeof value === 'boolean') return <p className="text-sm">{value ? 'Yes' : 'No'}</p>;
  if (value === null || value === undefined || value === '') {
    return <p className="text-sm GREY_400-CLR">-</p>;
  }
  return <p className="text-sm text-gray-900 whitespace-pre-wrap break-words">{String(value)}</p>;
};

const AnswerCell = ({ field, value }: Answer) => (
  <div className="flex flex-col gap-0.5 min-w-0">
    <span className="text-xs font-semibold text-gray-900">
      {field.name || field.id}
      {field.isRequired && <span className="text-red-500">*</span>}
    </span>
    <AnswerValue value={value} field={field} />
  </div>
);

// One template section's answers in rows of its column count, each row underlined — same as the
// credit application details page. A conditional question takes its own full row, with the
// chosen option's answers indented beneath it.
const AnswerRows = ({ answers, columns }: { answers: Answer[]; columns: number }) => {
  const rows: Answer[][] = [];
  let current: Answer[] = [];
  answers.forEach((answer) => {
    if (answer.conditional) {
      if (current.length) rows.push(current);
      rows.push([answer]);
      current = [];
      return;
    }
    current.push(answer);
    if (current.length === columns) {
      rows.push(current);
      current = [];
    }
  });
  if (current.length) rows.push(current);
  return (
    <>
      {rows.map((row, index) =>
        row[0]?.conditional ? (
          <div key={index} className="py-2 border-b border-gray-100 last:border-b-0">
            <AnswerCell field={row[0].field} value={row[0].value} />
            {row[0].conditional}
          </div>
        ) : (
          <div
            key={index}
            className={`grid ${
              GRID_COLS[columns] ?? GRID_COLS[1]
            } gap-x-6 gap-y-2 py-2 border-b border-gray-100 last:border-b-0`}>
            {row.map((answer) => (
              <AnswerCell key={answer.field.id} field={answer.field} value={answer.value} />
            ))}
          </div>
        )
      )}
    </>
  );
};

// The chosen option's fields, split into the sub-sections the template author built
// (sectionOrder + ungroupedSectionLayout), each with its own column count.
const conditionalSections = (
  fieldDef: ConditionalStepField
): { fields: AnswerField[]; columns: number }[] => {
  const order = fieldDef.sectionOrder?.length
    ? fieldDef.sectionOrder
    : [
        ...((fieldDef.groupedFields ?? []).length ? (['GROUPED'] as const) : []),
        ...((fieldDef.unGroupedFields ?? []).length ? (['NOT_GROUPED'] as const) : []),
      ];
  const ungrouped = fieldDef.unGroupedFields ?? [];
  const layout = fieldDef.ungroupedSectionLayout ?? [];
  let layoutIndex = 0;
  let offset = 0;
  let groupedDone = false;
  let ungroupedDone = false;
  const sections: { fields: AnswerField[]; columns: number }[] = [];
  order.forEach((type) => {
    if (type === 'GROUPED') {
      if (groupedDone) return;
      groupedDone = true;
      (fieldDef.groupedFields ?? [])
        .filter((group) => (group.groupedPosition ?? 0) === 0)
        .forEach((group) =>
          sections.push({ fields: group.fields, columns: COLUMN_COUNT[group.columnType] ?? 1 })
        );
      return;
    }
    if (layoutIndex < layout.length) {
      const { columnType, fieldCount } = layout[layoutIndex];
      layoutIndex += 1;
      sections.push({
        fields: ungrouped.slice(offset, offset + Number(fieldCount)),
        columns: COLUMN_COUNT[columnType] ?? 1,
      });
      offset += Number(fieldCount);
      return;
    }
    if (ungroupedDone || layout.length > 0) return;
    ungroupedDone = true;
    sections.push({ fields: ungrouped, columns: COLUMN_COUNT[fieldDef.columnType] ?? 1 });
  });
  return sections;
};

const toAnswers = (
  fields: AnswerField[],
  valueFor: (field: AnswerField) => { found: boolean; value: any; saved?: any },
  renderConditional: (field: AnswerField, saved: any) => React.ReactNode
): Answer[] =>
  fields
    .filter((field) => field.id && field.fromField !== 'DOCUMENT_FIELD')
    .flatMap((field) => {
      const { found, value, saved } = valueFor(field);
      if (!found) return [];
      return [{ field, value, conditional: renderConditional(field, saved) }];
    });

// Renders a submission's answers laid out like its template. Fields the signer never saw (hidden
// by a condition, or in an option that wasn't picked) aren't in fieldValues and are skipped.
const InternalFormAnswers = ({
  steps,
  fieldValues,
}: {
  steps: CreditApplicationStep[];
  fieldValues: CreditApplicationFieldsValues[];
}) => {
  const savedById = new Map(fieldValues.map((field) => [field.id, field]));

  const renderConditional = (field: AnswerField, saved?: CreditApplicationFieldsValues) => {
    const template = field as CreditApplicationFields;
    const fieldDef = saved?.value
      ? template.conditionalOptionFields?.[String(saved.value)]
      : undefined;
    if (template.fromField !== 'CONDITIONAL_SELECTED_FIELD' || !fieldDef) return undefined;
    const nested = new Map(
      (saved?.fieldValue ?? [])
        .filter((item: any) => !item.hidden)
        .map((item) => [item.id, item] as const)
    );
    const sections = conditionalSections(fieldDef)
      .map((section) => ({
        columns: section.columns,
        answers: toAnswers(
          section.fields,
          (nestedField) => {
            const item = nested.get(nestedField.id);
            return { found: !!item, value: item?.value };
          },
          () => undefined
        ),
      }))
      .filter((section) => section.answers.length > 0);
    if (sections.length === 0) return undefined;
    return (
      <div className="mt-2 ml-3 pl-3 border-l border-gray-200">
        {sections.map((section, index) => (
          <AnswerRows key={index} answers={section.answers} columns={section.columns} />
        ))}
      </div>
    );
  };

  return (
    <div className="flex flex-col">
      {[...steps]
        .sort((a, b) => a.position - b.position)
        .flatMap((step) =>
          [...step.fields]
            .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
            .map((section, index) => {
              const fields =
                section.stepType === 'GROUPED'
                  ? (section.groupedFields ?? [])
                      .filter((group) => (group.groupedPosition ?? 0) === 0)
                      .flatMap((group) => group.fields)
                  : section.unGroupedFields ?? [];
              const answers = toAnswers(
                fields,
                (field) => {
                  const saved = savedById.get(field.id);
                  return { found: !!saved, value: saved?.value, saved };
                },
                renderConditional
              );
              if (answers.length === 0) return null;
              return (
                <div
                  key={`${step.id}-${section.position ?? index}`}
                  className="border-b border-gray-100 last:border-b-0">
                  {section.groupName && (
                    <p className="text-sm font-semibold GREY_800-CLR pt-2">{section.groupName}</p>
                  )}
                  <AnswerRows answers={answers} columns={COLUMN_COUNT[section.columnType] ?? 1} />
                </div>
              );
            })
        )}
    </div>
  );
};

export default InternalFormAnswers;
